import { type SignInInput, signInInputSchema } from '@repo/api-contracts';
import { useState } from 'react';
import { useAuthStore } from '@/entities/user';
import { trpc } from '@/shared/api';
import { firstErrorPerField, getErrorMessage, mapServerValidationErrors } from '@/shared/lib';

const FIELD_KEYS = ['email', 'password'] as const;

type FieldErrors = Partial<Record<keyof SignInInput, string>>;

interface UseSignInResult {
  readonly fieldErrors: FieldErrors;
  readonly formError: string | null;
  readonly isPending: boolean;
  readonly submit: (input: SignInInput) => Promise<boolean>;
}

/**
 * 로그인 폼 로직.
 *
 * 클라이언트에서 먼저 Zod 로 검증해 불필요한 왕복을 줄이고,
 * 서버가 내려준 필드별 오류(fieldErrors)도 버리지 않고 같은 방식으로 매핑한다 —
 * 칸으로 옮길 오류가 없으면 폼 오류로 보인다(mapServerValidationErrors).
 */
export const useSignIn = (): UseSignInResult => {
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const setSession = useAuthStore((state) => state.setSession);
  const mutation = trpc.auth.signIn.useMutation();

  const submit = async (input: SignInInput): Promise<boolean> => {
    setFormError(null);

    const parsed = signInInputSchema.safeParse(input);

    if (!parsed.success) {
      setFieldErrors(firstErrorPerField(parsed.error.flatten().fieldErrors, FIELD_KEYS));
      return false;
    }

    setFieldErrors({});

    try {
      const session = await mutation.mutateAsync(parsed.data);
      setSession(session);
      return true;
    } catch (error) {
      const serverErrors = mapServerValidationErrors(error, FIELD_KEYS);

      if (serverErrors) {
        setFieldErrors(serverErrors.fieldErrors);
        setFormError(serverErrors.formError);
      } else {
        setFormError(getErrorMessage(error, '로그인에 실패했습니다.'));
      }

      return false;
    }
  };

  return { fieldErrors, formError, isPending: mutation.isPending, submit };
};
